import React, { useState, useEffect } from 'react';
import { usePackages } from '../context/PackageContext';
import '../styles/BookingForm.css';

const BookingForm = ({ tripTitle = 'Your Trip', onSubmit }) => {
  const { packages } = usePackages();
  const [selectedTrip, setSelectedTrip] = useState(tripTitle);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    date: '',
    adults: 1,
    hasChildren: false,
    childrenCount: 0,
    childrenAges: []
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Update selected trip when tripTitle prop changes
  useEffect(() => {
    setSelectedTrip(tripTitle);
  }, [tripTitle]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked,
        ...(name === 'hasChildren' && !checked ? { childrenCount: 0, childrenAges: [] } : {})
      }));
    } else if (name === 'childrenCount') {
      const count = parseInt(value) || 0;
      const ages = Array(count).fill('');
      setFormData(prev => ({
        ...prev,
        childrenCount: count,
        childrenAges: ages
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleChildAgeChange = (index, value) => {
    const newAges = [...formData.childrenAges];
    newAges[index] = value;
    setFormData(prev => ({
      ...prev,
      childrenAges: newAges
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.name.trim()) {
      alert('Please enter your name');
      return;
    }
    
    if (!formData.phone.trim()) {
      alert('Please enter your phone number');
      return;
    }
    
    if (!formData.date) {
      alert('Please select a date');
      return;
    }
    
    if (formData.hasChildren && formData.childrenCount > 0) {
      const allAgesFilled = formData.childrenAges.every(age => age !== '');
      if (!allAgesFilled) {
        alert('Please enter ages for all children');
        return;
      }
    }
    
    // Calculate total people
    const totalPeople = parseInt(formData.adults) + (formData.hasChildren ? parseInt(formData.childrenCount) : 0);
    
    setIsSubmitting(true);
    
    try {
      // Send email via PHP
      const response = await fetch('/send-booking.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          trip_title: selectedTrip,
          travel_date: formData.date,
          adults: formData.adults,
          children_count: formData.hasChildren ? formData.childrenCount : 0,
          children_ages: formData.childrenAges.length > 0 ? formData.childrenAges.join(', ') : 'N/A',
          total_people: totalPeople
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        alert(`✓ Booking Request Submitted!\n\nName: ${formData.name}\nTrip: ${selectedTrip}\nTotal People: ${totalPeople}\n\nWe'll contact you within 24 hours!`);
        
        // Reset form
        setFormData({
          name: '',
          phone: '',
          date: '',
          adults: 1,
          hasChildren: false,
          childrenCount: 0,
          childrenAges: []
        });
      } else {
        alert('Error: ' + (result.error || 'Failed to submit booking'));
      }
    } catch (error) {
      console.error('Submission error:', error);
      alert('Failed to submit booking. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="booking-form-modern">
      <div className="booking-form-header">
        <h3><i className="fas fa-clipboard-list"></i> Book {selectedTrip}</h3>
        <p>Fill in your details and we'll get back to you soon</p>
      </div>
      
      <form onSubmit={handleSubmit} className="booking-form-content">
        {/* Name Field */}
        <div className="form-group-modern">
          <label htmlFor="name">
            <i className="fas fa-user"></i> Full Name
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Enter your full name"
            required
          />
        </div>

        {/* Trip Selection */}
        <div className="form-group-modern">
          <label htmlFor="trip">
            <i className="fas fa-map-pin"></i> Select Trip
          </label>
          <select
            id="trip"
            value={selectedTrip}
            onChange={(e) => setSelectedTrip(e.target.value)}
            required
          >
            <option value="">Select a trip...</option>
            {packages && packages.length > 0 ? (
              packages.map((pkg, index) => (
                <option key={index} value={pkg.title}>
                  {pkg.title}
                </option>
              ))
            ) : (
              <option value={selectedTrip}>{selectedTrip}</option>
            )}
          </select>
        </div>

        {/* Phone Field */}
        <div className="form-group-modern">
          <label htmlFor="phone">
            <i className="fas fa-phone"></i> Phone Number
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="Enter your phone number"
            required
          />
        </div>

        {/* Date Field */}
        <div className="form-group-modern">
          <label htmlFor="date">
            <i className="fas fa-calendar-alt"></i> Travel Date
          </label>
          <input
            type="date"
            id="date"
            name="date"
            value={formData.date}
            onChange={handleChange}
            min={new Date().toISOString().split('T')[0]}
            required
          />
        </div>

        {/* Number of Adults */}
        <div className="form-group-modern">
          <label htmlFor="adults">
            <i className="fas fa-users"></i> Number of Adults
          </label>
          <select
            id="adults"
            name="adults"
            value={formData.adults}
            onChange={handleChange}
            required
          >
            <option value="1">1 Adult</option>
            <option value="2">2 Adults</option>
            <option value="3">3 Adults</option>
            <option value="4">4 Adults</option>
            <option value="5">5 Adults</option>
            <option value="6">6 Adults</option>
            <option value="7">7 Adults</option>
            <option value="8">8 Adults</option>
            <option value="9">9 Adults</option>
            <option value="10">10+ Adults</option>
          </select>
        </div>

        {/* Children Checkbox */}
        <div className="form-group-modern checkbox-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              name="hasChildren"
              checked={formData.hasChildren}
              onChange={handleChange}
            />
            <span className="checkbox-text">
              <i className="fas fa-child"></i> Traveling with children?
            </span>
          </label>
        </div>

        {/* Children Count (conditional) */}
        {formData.hasChildren && (
          <div className="children-section">
            <div className="form-group-modern">
              <label htmlFor="childrenCount">
                <i className="fas fa-baby"></i> Number of Children
              </label>
              <select
                id="childrenCount"
                name="childrenCount"
                value={formData.childrenCount}
                onChange={handleChange}
                required
              >
                <option value="0">Select number</option>
                <option value="1">1 Child</option>
                <option value="2">2 Children</option>
                <option value="3">3 Children</option>
                <option value="4">4 Children</option>
                <option value="5">5+ Children</option>
              </select>
            </div>

            {/* Children Ages (dynamic) */}
            {formData.childrenCount > 0 && (
              <div className="children-ages-section">
                <label className="ages-label">
                  <i className="fas fa-birthday-cake"></i> Children's Ages
                  {formData.childrenCount === 5 && ' (5+ Students)'}
                </label>
                <div className="ages-grid">
                  {formData.childrenAges.map((age, index) => (
                    <div key={index} className="age-input-group">
                      <label htmlFor={`child-age-${index}`}>Child {index + 1}</label>
                      <input
                        type="number"
                        id={`child-age-${index}`}
                        value={age}
                        onChange={(e) => handleChildAgeChange(index, e.target.value)}
                        placeholder="Age"
                        min="0"
                        max="17"
                        required
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Submit Button */}
        <button type="submit" className="btn-submit-booking" disabled={isSubmitting}>
          <i className={`fas ${isSubmitting ? 'fa-spinner fa-spin' : 'fa-paper-plane'}`}></i>
          {isSubmitting ? 'Submitting...' : 'Request Booking'}
        </button>

        <p className="booking-form-note">
          <i className="fas fa-info-circle"></i> We'll contact you within 24 hours to confirm your booking
        </p>
      </form>
    </div>
  );
};

export default BookingForm;
